import {SelectionChange, SelectionModel} from '@angular/cdk/collections';
import {Injectable, OnDestroy} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {ActivatedRoute, NavigationExtras, Router} from '@angular/router';
import {BehaviorSubject, combineLatest, Observable, of, Subject} from 'rxjs';
import {filter, first, flatMap, map, share, startWith, takeUntil, tap} from 'rxjs/operators';
import {DeleteValidationResponseObj} from '../../../../generated/generated-core.model';
import {capitalize} from '../../../utils/string-utils';
import {MxNotificationService} from '../../../shared/services/mx-notification.service';
import {MxUrlUtilsService} from '../../../shared/services/mx-url-utils.service';
import {MxConfirmDialogComponent} from '../../mx-dialogs/components/mx-confirm-dialog/mx-confirm-dialog.component';
import {MxFormMode} from '../../mx-form/services/mx-form-fields-builder.service';
import {MxPreloaderService, PreloaderConfig, withPreloader} from '../../mx-preloader/services/mx-preloader.service';
import {MxCollectionDataService} from './mx-collection-data.service';
import {
    MxGridCustomizerEvent,
    MxGridExtractId,
    MxGridHandleOrder,
    MxGridReload,
    MxGridRemoveConfig,
    MxGridSelectionChangedFn,
    MxGridWithThumbnail
} from './mx-grid-customizer-event';
import {SelectionStatus} from './selection-status';
import {MxSelection} from './selection-type.model';


export interface GridCollectionService extends MxGridExtractId<any>, MxGridReload {

    selection: SelectionModel<any>;

    lastSelectionChange: SelectionChange<any>;

    readonly rows: ReadonlyArray<any>;
    readonly selectionStatus: SelectionStatus;
    readonly selectedIndex: number;
    readonly fullSelection: ReadonlyArray<any>;
    readonly selectionLength: number;
    readonly notSelectedInitKeys: ReadonlyArray<any>;

    /** Set UI manipulated rows */
    setRows(row: Array<any>): void;

    setSelected(items: Array<any>): void;

    setInitialSelectedKeys(keys: ReadonlyArray<number | string>): void;

    /** Set the 'true' server side rows, usually  server side source */
    setRowsSource(rowsSource$: Observable<Array<any>>): void;

    /** Load rows from source */
    loadRows(): void;

    confirmRemove(confirm?: boolean, confirmMessage?: string, allowDelete?: boolean): Observable<boolean>;

    /** Remove rows */
    doRemove(selection?: any[], removeConfig?: MxGridRemoveConfig): void;

    /** Remove rows (without confirmation and REST request) */
    removeSelectedItemsFromGrid(): void;

    /** Notifier about rows changes */
    rowsChanged(): Observable<any>;

    /**
     * Notifier about selection change.
     *
     * Use this one and not selection.onChange
     */
    selectionChanged(): Observable<SelectionChange<any>>;

    goToItemDetails(id: number | 'new', formMode: MxFormMode, extras?: NavigationExtras): void;
}

@Injectable()
export class MxGridCollectionService implements GridCollectionService, OnDestroy {

    private _destroy$$: Subject<null> = new Subject<null>();

    private restLink: string;
    private itemDetailsLink: string;
    private detailWithParams: boolean;

    /** Grid rows */
    private rows$$: BehaviorSubject<Array<any>> = new BehaviorSubject<Array<any>>([]);

    /**
     * Allows to subscribe to rows changes, and service
     * will have the most updated the readonly copy _rows
     */
    private _rows$: Observable<Array<any>> = this.rows$$
        .pipe(
            takeUntil(this._destroy$$),
            tap((rows) => {
                this._rows = rows;
                this.restoreSelectionOnRowsLoaded();
            }),
            share());

    /** Latest copy of rows */
    _rows: ReadonlyArray<any>;

    /** Application rows source */
    private rowsSource$: Observable<Array<any>>;

    /** Grid selection */
    selection: SelectionModel<any> = new SelectionModel<any>(true, []);

    private _fullSelection: SelectionModel<any>;

    private _lastSelectionChange: SelectionChange<any>;

    /** Status of selection */
    private _selectionStatus: SelectionStatus = 'none';

    /** Selected index */
    private _selectedIndex: number = null;

    private _initialSelectedKeys: ReadonlyArray<number | string> = [];
    private _notSelectedInitKeys: ReadonlyArray<number | string> = [];

    private gridReload: MxGridReload;
    private extract: MxGridExtractId<any>;
    private removeConfig: MxGridRemoveConfig;
    private thumbnailData: MxGridWithThumbnail;
    private orderObjectHandler: MxGridHandleOrder<any>;
    private itemDetailsLinkRelative: boolean;
    private gridSelectionChangedFn: MxGridSelectionChangedFn;
    private preloaderConfig: PreloaderConfig;
    protected _invalidRows: Map<number, Map<string, string>> = new Map();
    private boldRow: (row: any) => boolean;

    get rows(): ReadonlyArray<any> {
        return this._rows || [];
    }

    setRows(rows: Array<any>): void {
        this.rows$$.next(rows);
    }

    setSelected(items: Array<any>): void {
        this.selection.select(...items);
    }

    setInitialSelectedKeys(keys: ReadonlyArray<number | string>): void {
        this._initialSelectedKeys = keys;
    }

    public rowsChanged(): Observable<Array<any>> {
        return this._rows$;
    }

    private restoreSelectionOnRowsLoaded(): void {
        if (this._rows.length > 0) {
            // Set selected items by key when rows loaded
            let initialSelectedItems = this._rows.filter(row => this._initialSelectedKeys.indexOf(this.extractId(row)) > -1);
            let oldFullSelection = this._fullSelection ? this._fullSelection.selected
                .filter(row => initialSelectedItems.findIndex(value => this.extractId(row) === this.extractId(value)) === -1) : [];
            this._fullSelection = new SelectionModel<any>(true, [...initialSelectedItems,
                ...oldFullSelection]);
            let selectedIds = this._fullSelection.selected.map(row => this.extractId(row));
            let selectedItems = this._rows.filter(row => selectedIds.indexOf(this.extractId(row)) > -1);
            this._notSelectedInitKeys = this._initialSelectedKeys.filter(value => !selectedIds.includes(value));

            let fullSelection = this._fullSelection.selected;
            this.selection.clear();
            this.setSelected(selectedItems);
            this._fullSelection = new SelectionModel<any>(true, fullSelection);
        } else {
            this.selection.clear();
        }
    }

    get selectedIndex(): number {
        return this._selectedIndex;
    }

    get fullSelectionModel(): SelectionModel<any> {
        if (this._fullSelection) {
            return this._fullSelection;
        }
        return new SelectionModel();
    }

    get fullSelection(): ReadonlyArray<any> {
        if (this._fullSelection) {
            return this._fullSelection.selected;
        }
        return [];
    }

    get selectionLength(): number {
        let length = 0;
        if (this._fullSelection && this._fullSelection.selected) {
            length += this._fullSelection.selected.length;
        }
        if (this._notSelectedInitKeys) {
            length += this._notSelectedInitKeys.length;
        }
        return length;
    }

    get notSelectedInitKeys(): ReadonlyArray<any> {
        return [...this._notSelectedInitKeys];
    }

    get lastSelectionChange(): SelectionChange<any> {
        return this._lastSelectionChange;
    }

    /**
     * Allows to subscribe to selection change.
     *
     * Use always this one and not selection.onChange
     */
    selectionChanged(): Observable<SelectionChange<any>> {
        return this.selection.changed
            .pipe(takeUntil(this._destroy$$), share());
    }

    private updateSelectionStatus(change: SelectionChange<any>): void {
        this._lastSelectionChange = change;
        this._selectionStatus = MxSelection.selectionStatus(this.selection);
        if (this.selection.selected[0]) {
            let sortedRow = this.rows.slice()
                .sort((a, b) => this.orderObjectHandler.getOrder(a) - this.orderObjectHandler.getOrder(b));
            this._selectedIndex = sortedRow.indexOf(this.selection.selected[0]);
        } else {
            this._selectedIndex = null;
        }
    }

    private updateFullSelection(change: SelectionChange<any>): void {
        if (change.added.length > 0) {
            let selectedIds = this._fullSelection.selected.map(row => this.extractId(row));
            let newItems = change.added.filter(row => selectedIds.indexOf(this.extractId(row)) < 0);
            this._fullSelection.select(...newItems);

        }
        if (change.removed.length > 0) {
            let deselectedIds = change.removed.map(row => this.extractId(row));
            let deselectedItems = this._fullSelection.selected.filter(row => deselectedIds.indexOf(this.extractId(row)) > -1);
            this._fullSelection.deselect(...deselectedItems);
        }
    }

    rowsOrSelectionChanged(): Observable<GridCollectionService> {

        return combineLatest(
            [
                this.rowsChanged(),
                // need initial value of selection
                this.selectionChanged().pipe(startWith({source: this.selection}))
            ])
            .pipe(
                takeUntil(this._destroy$$),
                map(() => this));
    }

    constructor(private dataService: MxCollectionDataService,
                private dialog: MatDialog,
                private notificationService: MxNotificationService,
                private route: ActivatedRoute,
                private router: Router,
                private urlService: MxUrlUtilsService,
                private preloader: MxPreloaderService) {
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    public setUp(event: MxGridCustomizerEvent): void {

        this.gridReload = event.gridReload;
        this.extract = event.extractId;
        this.thumbnailData = event.thumbnailData;
        this.orderObjectHandler = event.orderCustomizer;
        this.itemDetailsLinkRelative = event.gridProps.routeLinkRelative;
        this.gridSelectionChangedFn = event.selectionChangedFn;
        this.preloaderConfig = event.preloaderConfig;
        if (event.restUrl) {
            this.restLink = this.urlService.constructUrl(event.restUrl, this.route);
        }
        if (event.itemDetailsLink) {
            this.itemDetailsLink = event.itemDetailsLink;
        }
        this.detailWithParams = event.gridProps.detailWithParams;
        this.rowsSource$ = event.rowsSource$ || this.dataService.getAll(this.restLink); // TODO add spinner
        this.boldRow = event.boldRow;
        this.removeConfig = Object.assign(
            // default config goes to data service
            {
                doRemove:
                    (idsToRemove: number[]) => this.dataService.delete(this.restLink, idsToRemove),
                validateRemove: (idsToRemove: number[], shouldValidate: boolean) => {
                    if (shouldValidate) {
                        return this.dataService.validateRemove(this.restLink, idsToRemove);
                    } else {
                        return of({message: undefined, allowDelete: true});
                    }
                }
            }, event.removeConfig);
        if (event.gridProps.handleGridErrors) {
            event.rowErrorsSource
                .pipe(takeUntil(this._destroy$$))
                .subscribe(errorMap => {
                    this._invalidRows = errorMap;
                });
        }
        this.selectionChanged().subscribe((s) => {
            this.updateSelectionStatus(s);
            this.updateFullSelection(s);

            if (this.gridSelectionChangedFn) {
                this.gridSelectionChangedFn(s, this.selection);
            }
        });
    }

    get selectionStatus(): SelectionStatus {
        return this._selectionStatus;
    }

    extractId(row: any): number | string {
        return this.extract.extractId(row);
    }


    getThumbnailClass(row: any): string {
        return this.thumbnailData.getThumbnailClass(row);
    }

    getThumbnailUrl(row: any): string {
        return this.thumbnailData.getThumbnailUrl(row);
    }

    getThumbnailColumnName(): string {
        if (this.thumbnailData) {
            return this.thumbnailData.getThumbnailColumnName();
        }
        return null;
    }

    public setRowsSource(rowsSource$: Observable<Array<any>>): void {
        this.rowsSource$ = rowsSource$;
    }

    public loadRows(): void {
        this.rowsSource$.pipe(
            takeUntil(this._destroy$$),
            withPreloader(this.preloader, this.preloaderConfig)
        ).subscribe(
                (data: Array<any>) => this.rows$$.next(data),
                (error) => this.notificationService.showFailure(
                    this.notificationService.getErrorMessage(error, 'Error while loading rows'))
            );
    }

    public doReload(): void {
        if (this.gridReload) {
            this.gridReload.doReload();
        } else {
            this.loadRows();
        }
    }

    public confirmRemove(confirm: boolean = this.removeConfig.confirm,
                         confirmMessage: string | boolean = this.removeConfig.confirmMessage(this.selection.selected),
                         allowDelete: boolean = true): Observable<boolean> {
        if (confirm && typeof  confirmMessage === 'string') {
            let removeConfirmDialog = this.dialog.open(MxConfirmDialogComponent, {
                panelClass: 'mx-dialog-panel-class',
                data: {
                    message: confirmMessage,
                    title: 'Remove',
                    type: 'warn',
                    isAlert: !allowDelete
                }
            });
            return removeConfirmDialog.afterClosed().pipe(map((r: boolean) => r && allowDelete));
        } if (typeof confirmMessage === 'boolean') {
            return of(confirmMessage);
        } else {
            return of(true);
        }
    }


    public doRemove(selectionArg: any[] = this.selection.selected,
                    deleteConfig: MxGridRemoveConfig = this.removeConfig): void {

        const selection = selectionArg;

        if (selection.length <= 0) {
            return;
        }

        let selectedIds = selection.map((row: any) => this.extractId(row));

        let validation$: Observable<DeleteValidationResponseObj> = deleteConfig.validateRemove(selectedIds, deleteConfig.shouldValidate);

        let remove$: Observable<Array<number | string>> = validation$.pipe(
            flatMap((res) => {
                let message = deleteConfig.confirmMessage(selection);
                if (res.message && res.message !== undefined) {
                    message = res.message;
                }
                return this.confirmRemove(deleteConfig.confirm, message, res.allowDelete);
            }),
            first(),
            filter((ok) => ok),
            flatMap(() => deleteConfig.doRemove(selectedIds)),
            tap(() => this.removeSelectedItemsFromGrid())
        );
        this.notificationService.notify(remove$,
            `${capitalize(deleteConfig.recordsName)} were removed`,
            `Error with remove ${deleteConfig.recordsName}`)
            .subscribe();
    }

    public removeSelectedItemsFromGrid(): void {
        let filterSelected = this.rows.filter((val) => {
            return !this.selection.isSelected(val);
        });
        this.setRows(filterSelected);
        this.selection.clear();
        this._fullSelection.clear();
    }

    public goToItemDetails(id: number | string | 'new', formMode: MxFormMode,
                           extras: NavigationExtras = {}, detailWithParams?: boolean): void {
        if (id === undefined) {
            console.debug('Cannot go to item with undefined id');
            return;
        }
        if (this.detailWithParams || detailWithParams) {
            extras.queryParams = {...extras.queryParams, formMode: formMode};
        }
        if (this.itemDetailsLink) {
            extras.relativeTo = this.itemDetailsLinkRelative ? this.route : null;
            this.router.navigate([this.itemDetailsLink, id], extras);
        } else {
            extras.relativeTo = this.route;
            this.router.navigate([id], extras);
        }
    }

    public getItemDetailsLink(id: number | 'new'): string {
        if (id === undefined) {
            console.debug('Cannot go to item with undefined id');
            return '';
        }
        const extras: NavigationExtras = {};
        if (this.itemDetailsLink) {
            extras.relativeTo = this.itemDetailsLinkRelative ? this.route : null;
            return this.router.createUrlTree([this.itemDetailsLink, id], extras).toString();
        } else {
            extras.relativeTo = this.route;
            return this.router.createUrlTree([id], extras).toString();
        }
    }

    public clearErrors(): void {
        this._invalidRows.clear();
    }

    public isRowWithError(index: number): boolean {
        return this._invalidRows.has(index);
    }

    public isRowBold(row: any): boolean {
        if (!this.boldRow) {
            return false;
        }
        return this.boldRow(row);
    }

    public getErrorByIndex(index: number, column: string): string | boolean {
        let rowError = this._invalidRows.get(index);
        if (rowError) {
            return rowError.get(column);
        } else {
            return false;
        }
    }
}

